import { Skeleton } from "antd";

import {
  getEmployeeHeadCount,
  getEmployeeNationalityMix,
  getEmployeeStatstics,
  getWorkforceComposition,
} from "api/services/dashboard";
import { AgePyramid, CountBars, HeadcountChart, MonthlyLeaversChart } from "./workforce/charts";
import { OverviewKpis } from "./workforce/KpiTiles";
import {
  CostCenterBars,
  DepartmentTreemap,
  DesignationBars,
  GenderPie,
  NationalityBars,
  NewEmployeesLine,
} from "./workforce/overviewCharts";
import { DataCompleteness, TopManagers } from "./workforce/PeopleLists";
import StatCard, { Section } from "./workforce/StatCard";
import { fromHeadcountRows } from "./workforce/headcountRows";
import { useStatsData } from "./workforce/useStatsData";

/** The old page's three endpoints (same data as before) plus the composition
 *  endpoint for the new cards, fetched together. */
const fetchOverview = (params) =>
  Promise.all([
    getEmployeeStatstics(params),
    getEmployeeNationalityMix(params),
    getEmployeeHeadCount(null, params),
    getWorkforceComposition(params),
  ]).then(([stats, nationality, headcount, composition]) => ({
    data: {
      stats: stats.data,
      nationality: nationality.data || [],
      monthly: fromHeadcountRows(headcount.data),
      composition: composition.data,
    },
  }));

const statusRows = (stats) =>
  (stats?.grouped_by_employment_status || []).map((s) => ({ name: s.name || "Not recorded", count: s.count }));

/** Every card of the original page, same titles, chart types and order. */
const OriginalCards = ({ stats, nationality, monthly }) => (
  <Section title="Overview">
    <div className="ws-grid ws-grid--3">
      <StatCard title="Employee by Department Statistics" hint="Click a department to open its employees">
        <DepartmentTreemap departments={stats?.grouped_by_department} />
      </StatCard>
      <StatCard title="Gender Distribution" hint="Click a slice to open those employees">
        <GenderPie genders={stats?.grouped_by_gender} />
      </StatCard>
      <StatCard title="Employment Status" hint="Current employees">
        <CountBars items={statusRows(stats)} labelKey="name" horizontal label="Employees" />
      </StatCard>
    </div>
    <StatCard title="Nationality & Gender Distribution" hint="Click a bar to open those employees">
      <NationalityBars data={nationality} />
    </StatCard>
    <StatCard title="Employee by Designation" hint="Click a bar to open those employees">
      <DesignationBars positions={stats?.grouped_by_position} />
    </StatCard>
    <StatCard title="Cost Center Overview" hint="Click a bar to open those employees">
      <CostCenterBars costCenters={stats?.cost_center} />
    </StatCard>
    <div className="ws-grid ws-grid--2">
      <StatCard title="New Employees" hint="Joiners per month; the current month is to date">
        <NewEmployeesLine trend={monthly} />
      </StatCard>
      <StatCard title="Resigned/Terminated/Non-Renewal" hint="Leavers per month by BC reason code">
        <MonthlyLeaversChart trend={monthly} />
      </StatCard>
    </div>
    <StatCard title="Employee Head Count" hint="Employees per month; hover for joiners and leavers">
      <HeadcountChart trend={monthly} />
    </StatCard>
  </Section>
);

const NewCards = ({ composition }) => {
  const span = composition.span_of_control;
  return (
    <Section title="More insights">
      <div className="ws-grid ws-grid--3">
        <StatCard title="Tenure" hint="Current employees by time with the company">
          <CountBars items={composition.tenure} label="Employees" />
        </StatCard>
        <StatCard title="Age and gender" hint="Where date of birth is known">
          <AgePyramid bands={composition.age_pyramid} />
        </StatCard>
        <StatCard title="Grade" hint="Top grades">
          <CountBars items={composition.grade_mix} labelKey="name" horizontal label="Employees" />
        </StatCard>
      </div>
      <div className="ws-grid ws-grid--2">
        <StatCard title="Profile completeness" hint="Share of current employees with each field filled in">
          <DataCompleteness fields={composition.data_completeness} />
        </StatCard>
        <StatCard title="Span of control" hint={`${span.managers} managers · ${span.average_reports ?? "-"} direct reports on average`}>
          <CountBars items={span.distribution} label="Managers" height={180} />
          <h4 className="ws-subtitle">Most direct reports</h4>
          <TopManagers managers={span.top} />
        </StatCard>
      </div>
    </Section>
  );
};

/** Employee Statistics > Overview tab. */
const OverviewTab = ({ filters }) => {
  const { loading, error, data } = useStatsData(fetchOverview, filters);
  if (error) return <div className="alert alert-danger" role="alert">{error}</div>;
  if (!data) return <Skeleton active paragraph={{ rows: 12 }} />;
  return (
    <div className={`ws-content ${loading ? "is-loading" : ""}`} aria-busy={loading}>
      <OverviewKpis stats={data.stats} summary={data.composition?.summary} />
      <OriginalCards stats={data.stats} nationality={data.nationality} monthly={data.monthly} />
      <NewCards composition={data.composition} />
    </div>
  );
};

export default OverviewTab;
